import React from 'react';
import { pillStyle } from './SecondaryAxisPills';
import { InfoTooltip } from './InfoTooltip';
import { zoneMeta, widestZoneLabel } from '../services/replyEngineMeta';

// D3 reply-engine hand-off zone chip (GRÜN/GELB/ROT — exactly three by doctrine; suppression
// is a reason, never a fourth zone). Fail-open: unknown zone strings render raw + neutral.
// InfoTooltip carries the zone legend so the chip is self-explaining without a legend panel.
//
// zone is null/undefined when the tenant's HANDOFF_ZONES lever is off — the envelope omits the
// key rather than shipping null. The honest render is NO chip, but the three call sites place
// this in a flex row where a disappearing chip would shift every sibling (UI Layout Stability
// Rule) — so absent renders the SAME span, same box, just invisible: visibility:hidden +
// aria-hidden, holding the widest doctrine label's width rather than the old neutral '—' text.
//
// Pill house rule shape-borrower (chip register sweep, Part C wave 2, 2026-09-09, see the
// exception note in SecondaryAxisPills.tsx): the METRICS (fontSize/padding/radius/etc, keyed by
// the 'row' variant) come from `pillStyle('info', 'row')`, but the colour stays the hand-off
// zone's own — the doctrine label ("GRÜN"/"GELB"/"ROT") is already the exact face wanted, so it
// does not route through `pillFace`. Both branches below spread the SAME base object so the
// width-reservation contract (the ZoneChip test suite pins this) still holds byte-for-byte.
export const ZoneChip: React.FC<{ zone: string | null | undefined; style?: React.CSSProperties }> = ({ zone, style }) => {
  if (zone === null || zone === undefined) {
    return (
      <span aria-hidden="true" style={{
        ...pillStyle('info', 'row'),
        fontWeight: 700, letterSpacing: 0.4,
        border: '1px solid transparent',
        display: 'inline-block', visibility: 'hidden',
        ...style,
      }}>
        {widestZoneLabel()}
      </span>
    );
  }
  const meta = zoneMeta(zone);
  // Fix wave M4: the legend rides the shared InfoTooltip, not native title= — same span, same
  // box, so the invisible-placeholder branch above still holds the identical width. A zone whose
  // meta carries no legend (the fail-open raw-string render) gets the bare chip: an empty tooltip
  // would add a focus stop that says nothing.
  const chip = (
    <span style={{
      ...pillStyle('info', 'row'),
      fontWeight: 700, letterSpacing: 0.4,
      color: meta.fg, backgroundColor: meta.bg, border: `1px solid ${meta.border}`,
      display: 'inline-block',
      ...style,
    }}>
      {meta.label}
    </span>
  );
  return meta.legend ? <InfoTooltip text={meta.legend}>{chip}</InfoTooltip> : chip;
};
